"use client";

import { createClientFeature } from "@payloadcms/richtext-lexical/client";
import {
  $createHeadingNode,
  $isHeadingNode,
} from "@payloadcms/richtext-lexical/lexical/rich-text";
import { useLexicalComposerContext } from "@payloadcms/richtext-lexical/lexical/react/LexicalComposerContext";
import { $findMatchingParent } from "@payloadcms/richtext-lexical/lexical/utils";
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
} from "@payloadcms/richtext-lexical/lexical";
import type { HeadingTagType } from "@payloadcms/richtext-lexical/lexical/rich-text";
import type { LexicalEditor, BaseSelection } from "@payloadcms/richtext-lexical/lexical";
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import {
  $createCustomHeadingNode,
  $isCustomHeadingNode,
  CustomHeadingNode,
  type HeadingFontSizeClass,
} from "./CustomHeadingNode";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const HEADING_TAGS: HeadingTagType[] = ["h1", "h2", "h3", "h4", "h5", "h6"];

const LABEL_MAP: Record<HeadingTagType, string> = {
  h1: "H1 Style",
  h2: "H2 Style",
  h3: "H3 Style",
  h4: "H4 Style",
  h5: "H5 Style",
  h6: "H6 Style",
};

/**
 * Returns the heading ancestor of the current selection — either a
 * CustomHeadingNode ("heading-styled") or a plain HeadingNode ("heading").
 * Both are accepted because CustomHeadingNode extends HeadingNode.
 */
function getSelectedHeadingNode(): CustomHeadingNode | ReturnType<typeof $createHeadingNode> | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return null;

  for (const node of selection.getNodes()) {
    const ancestor = $findMatchingParent(
      node,
      (n) => $isElementNode(n) && !n.isInline(),
    );
    if ($isHeadingNode(ancestor)) {
      // $isHeadingNode uses instanceof HeadingNode — true for CustomHeadingNode too
      return ancestor as CustomHeadingNode | ReturnType<typeof $createHeadingNode>;
    }
  }
  return null;
}

// ─── Toolbar icon: active font-size class (H1 … H6), not the semantic tag ───
// Payload renders <ChildComponent /> with no props — use LexicalComposerContext.

const NO_FONT_CLASS_LABEL = "—";

function HeadingLevelDropdownIcon(): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [label, setLabel] = useState(NO_FONT_CLASS_LABEL);

  const syncLabel = useCallback(() => {
    editor.getEditorState().read(() => {
      const heading = getSelectedHeadingNode();
      const fontClass =
        heading && $isCustomHeadingNode(heading)
          ? heading.getFontSizeClass()
          : null;
      if (fontClass) {
        setLabel(fontClass.toUpperCase());
      } else {
        setLabel(NO_FONT_CLASS_LABEL);
      }
    });
  }, [editor]);

  useEffect(() => {
    syncLabel();
    return editor.registerUpdateListener(() => {
      syncLabel();
    });
  }, [editor, syncLabel]);

  return (
    <span
      aria-hidden
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "1.35em",
        fontSize: "11px",
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: "-0.02em",
        color: "currentColor",
        fontFamily: "system-ui, sans-serif",
        userSelect: "none",
      }}
    >
      {label}
    </span>
  );
}

function isInsideHeading(selection: BaseSelection | null): boolean {
  if (!$isRangeSelection(selection)) return false;
  for (const node of selection.getNodes()) {
    const ancestor = $findMatchingParent(
      node,
      (n) => $isElementNode(n) && !n.isInline(),
    );
    if ($isHeadingNode(ancestor)) return true;
  }
  return false;
}

/**
 * Swap a plain HeadingNode → CustomHeadingNode with the given class,
 * or update the fontSizeClass on an existing CustomHeadingNode.
 * Returns to a plain HeadingNode when fontSizeClass is null.
 */
function applyFontClass(
  heading: ReturnType<typeof $createHeadingNode> | CustomHeadingNode,
  fontSizeClass: HeadingFontSizeClass,
): void {
  if ($isCustomHeadingNode(heading)) {
    if (!fontSizeClass) {
      // Remove override: swap back to a plain HeadingNode
      const plain = $createHeadingNode(heading.getTag());
      plain.setFormat(heading.getFormatType());
      plain.setIndent(heading.getIndent());
      plain.setDirection(heading.getDirection());
      for (const child of heading.getChildren()) plain.append(child);
      heading.replace(plain);
    } else {
      heading.setFontSizeClass(fontSizeClass);
    }
  } else {
    if (!fontSizeClass) return; // already plain, nothing to do
    // Upgrade plain HeadingNode → CustomHeadingNode
    const custom = $createCustomHeadingNode(heading.getTag(), fontSizeClass);
    custom.setFormat(heading.getFormatType());
    custom.setIndent(heading.getIndent());
    custom.setDirection(heading.getDirection());
    for (const child of heading.getChildren()) custom.append(child);
    heading.replace(custom);
  }
}

// ─── Toolbar group (shared between fixed + inline) ────────────────────────────

const toolbarGroups = [
  {
    type: "dropdown" as const,
    ChildComponent: HeadingLevelDropdownIcon,
    isEnabled: ({ selection }: { selection: BaseSelection }) =>
      isInsideHeading(selection),
    items: [
      // "Default" – removes any class override
      {
        key: "fontClass-default",
        label: "Default Style",
        isActive: ({ selection }: { selection: BaseSelection }) => {
          if (!$isRangeSelection(selection)) return false;
          const heading = getSelectedHeadingNode();
          if (!heading) return false;
          // Active when it's a plain HeadingNode (no custom class)
          return !$isCustomHeadingNode(heading) || !heading.getFontSizeClass();
        },
        onSelect: ({ editor }: { editor: LexicalEditor; isActive: boolean }) => {
          editor.update(() => {
            const heading = getSelectedHeadingNode();
            if (heading) applyFontClass(heading, null);
          });
        },
        order: 0,
      },
      // One item per heading level
      ...HEADING_TAGS.map((tag, i) => ({
        key: `fontClass-${tag}`,
        label: LABEL_MAP[tag],
        isActive: ({ selection }: { selection: BaseSelection }) => {
          if (!$isRangeSelection(selection)) return false;
          const heading = getSelectedHeadingNode();
          if (!$isCustomHeadingNode(heading)) return false;
          return heading.getFontSizeClass() === tag;
        },
        onSelect: ({
          editor,
          isActive,
        }: {
          editor: LexicalEditor;
          isActive: boolean;
        }) => {
          editor.update(() => {
            const heading = getSelectedHeadingNode();
            if (!heading) return;
            // Toggle off when clicking the already-active style
            const nextClass = isActive ? null : (tag as HeadingFontSizeClass);
            applyFontClass(heading, nextClass);
          });
        },
        order: i + 1,
      })),
    ],
    key: "heading-font-class",
    order: 28,
  },
];

// ─── Client Feature ───────────────────────────────────────────────────────────

export const HeadingClassFeatureClient = createClientFeature({
  // Register as a brand-new node type — no replacement, no conflict
  nodes: [CustomHeadingNode],

  toolbarFixed: { groups: toolbarGroups },
  toolbarInline: { groups: toolbarGroups },
});
